import { FC, useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { alertCircle } from "ionicons/icons"; // still using an icon if desired
import Icon from "./Icon";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";

let globalIgnoreClick = false;

interface Option {
  text: string;
  id: string;
}

interface SelectFieldProps {
  value: string | number;
  options: Option[];
  placeholder: string;
  onChange(value: string): void;
  error?: string;
  icon?: string;
  label?: string;
}

const DRAG_THRESHOLD = 5; // px distance to differentiate "click" vs. "drag"

const SelectField: FC<SelectFieldProps> = ({
  placeholder,
  value,
  options,
  error,
  icon,
  label,
  onChange,
}) => {
  const [showModal, setShowModal] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // DRAG state
  let isDragging = false;
  let startY = 0;
  let startScrollTop = 0;
  let totalDelta = 0; // track how far we've moved

  // Attach pointer events for "drag to scroll" only on devices with fine pointer (e.g., mouse/touchpad)
  useEffect(() => {
    // Check for coarse pointer (touchscreen) devices
    if (window.matchMedia("(pointer: coarse)").matches) {
      // On touch devices, native scrolling is generally preferable.
      return;
    }

    const el = modalContentRef.current;
    if (!el) return;

    const handlePointerDown = (e: PointerEvent) => {
      isDragging = true;
      startY = e.clientY;
      startScrollTop = el.scrollTop;
      totalDelta = 0;
      el.style.cursor = "grabbing";
      e.preventDefault(); // prevent text selection
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (!isDragging) return;
      const delta = e.clientY - startY;
      totalDelta = Math.abs(delta);
      el.scrollTop = startScrollTop - delta;
    };

    const handlePointerUp = () => {
      isDragging = false;
      el.style.cursor = "grab";
    };

    el.addEventListener("pointerdown", handlePointerDown);
    el.addEventListener("pointermove", handlePointerMove);
    el.addEventListener("pointerup", handlePointerUp);
    el.addEventListener("pointerleave", handlePointerUp);
    el.addEventListener("pointercancel", handlePointerUp);

    return () => {
      el.removeEventListener("pointerdown", handlePointerDown);
      el.removeEventListener("pointermove", handlePointerMove);
      el.removeEventListener("pointerup", handlePointerUp);
      el.removeEventListener("pointerleave", handlePointerUp);
      el.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [showModal]);

  // Handle item "click" with a threshold check
  const handleItemClick = (optionId: string) => {
    if (totalDelta > DRAG_THRESHOLD) {
      return;
    }
    onChange(optionId);
    setShowModal(false);
    // Temporarily disable the select field to prevent immediate reopening
    globalIgnoreClick = true;
    setTimeout(() => {
      globalIgnoreClick = false;
    }, 300);
  };

  // Determine if the device is a touch device (coarse pointer)
  const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

  return (
    <>
      <StyledTextField
        error={error}
        onClick={() => {
          if (!globalIgnoreClick) {
            setShowModal(true);
          }
        }}
      >
        {icon && <FieldIcon icon={icon} />}
        <Field>
          {label && <Label>{label}</Label>}
          <SelectedValue $error={!!error}>
            {value ? options.find((o) => o.id === value)?.text : placeholder}
          </SelectedValue>
        </Field>
        {error && <ErrorIcon icon={alertCircle} />}
      </StyledTextField>
  
      {showModal &&
        createPortal(
          <ModalBackdrop onClick={() => setShowModal(false)}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
              <ModalWrapper>
                <ModalHeader>{t("common_dropdown_title")}</ModalHeader>
                <ModalContent ref={modalContentRef}>
                  <div>
                    {options.map((option) => (
                      <ListItemWrapper
                        key={option.id}
                        {...(isTouchDevice
                          ? { onClick: () => handleItemClick(option.id) }
                          : {
                              onPointerUp: (e) => {
                                e.stopPropagation();
                                handleItemClick(option.id);
                              },
                            })}
                      >
                        {option.text}
                      </ListItemWrapper>
                    ))}
                  </div>
                </ModalContent>
                <StyledCloseButton onClick={() => setShowModal(false)}>
                  {t("common_dropdown_close_button")}
                </StyledCloseButton>
              </ModalWrapper>
            </ModalContainer>
          </ModalBackdrop>,
          document.body
        )}
    </>
  );
  
};

export default SelectField;

const StyledTextField = styled.div<{ error?: string }>`
  display: flex;
  align-items: center;
  background: var(--app-color-background, white);
  padding: 14px 10px;
  border-bottom: 1px solid var(--app-color-text, #444444);
  font-size: 19px;
  color: ${(props) => (props.error ? "red" : "var(--app-color-text, black)")};
  cursor: pointer;
`;

const FieldIcon = styled(Icon)`
  color: var(--app-color-text, #444444);
  font-size: 20px;
`;

const Field = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Label = styled.div`
  font-size: 20px;
  color: var(--app-color-text, #000);
  margin-bottom: 8px;
`;

const SelectedValue = styled.div<{ $error?: boolean }>`
  font-size: 16px;
  color: ${(props) => (props.$error ? "red" : "var(--app-color-text, #444)")};
  padding: 5px 10px;
`;

const ErrorIcon = styled(Icon)`
  color: ${(props) => props.theme.danger.main};
  font-size: 22px;
`;

/* Custom Modal Components */

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
`;

const ModalContainer = styled.div`
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: min(600px, 80%);
`;

const ModalWrapper = styled.div`
  background-color: var(--app-color-background, white);
  border-radius: 18px;
  width: 100%;
  min-height: 300px;
  max-height: 80vh;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 18px 36px;
  font-size: 18px;
  font-weight: bold;
  color: var(--app-color-text, #444);
  background: var(--app-color-surface-tint, #f0f0f0);
  border-bottom: 1px solid #ccc;
  border-radius: 18px 18px 0 0;
`;

const ModalContent = styled.div`
  flex-grow: 1;
  background: var(--app-color-background, white);
  overflow-y: auto;
  cursor: grab;
  max-height: calc(80vh - 140px); /* subtract header and button heights */
  border-radius: 4px;
  margin: 20px;
`;

const StyledCloseButton = styled.button`
  font-size: 18px;
  font-weight:bold;
  margin: 0 20px 20px;
  padding: 0 20px;
  height: 40px;
  background: var(--app-color-surface, #cdebf3);
  border: none;
  border-radius: 18px;
  color: var(--app-color-text, black);
  text-transform: none;
  font-family: var(--app-font-family, "Ciutadella Rounded", sans-serif);
  cursor: pointer;
`;

const ListItemWrapper = styled.div`
  padding: 16px;
  border-bottom: 1px solid #ccc;
  &:active {
    /* Option press = answer selection state -> accent (tokens.ts roles). */
    background-color: rgba(var(--app-color-accent-rgb, 66, 133, 244), 0.1);
  }
  &:hover {
    background-color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.04);
  }
`;

// Exported for ModalKit's ModalFields, which restyles this underlined trigger
// row into the same flat bordered field as the dialog text fields.
export { StyledTextField as SelectFieldTrigger };
